import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccountStore } from '../store/useAccountStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface AuthContextType {
  isCheckingAuth: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const { user, clearUser, loginTimestamp } = useAccountStore();
  const navigate = useNavigate();

  /**
   * Handle session expiry by clearing user state and redirecting
   */
  const handleSessionExpiry = () => {
    console.log('Session expired, clearing user session');

    // Clear the user account store
    clearUser();

    // Clear any stored session data
    localStorage.removeItem('account-storage');

    // Show logout message
    toast.info('Your session has expired. Please log in again.', {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

    // Redirect to login page
    navigate('/login');
  };

  /**
   * Manual logout function
   */
  const logout = () => {
    clearUser();
    localStorage.removeItem('account-storage');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  /**
   * Auto-logout after 30 minutes of login
   */
  useEffect(() => {
    if (!user || !loginTimestamp) {
      return;
    }

    const THIRTY_MINUTES = 15 * 60 * 1000; // 30 minutes in milliseconds
    const timeElapsed = Date.now() - loginTimestamp;
    const timeRemaining = THIRTY_MINUTES - timeElapsed;

    // If already past 30 minutes, logout immediately
    if (timeRemaining <= 0) {
      console.log('Session expired (30 minutes), logging out');
      handleSessionExpiry();
      return;
    }

    // Set timeout for remaining time
    const timeoutId = setTimeout(() => {
      console.log('Auto-logout triggered after 30 minutes');
      clearUser();
      localStorage.removeItem('account-storage');
      toast.info('Your session has expired. Please log in again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      navigate('/login');
    }, timeRemaining);

    // Cleanup timeout on unmount or when dependencies change
    return () => clearTimeout(timeoutId);
  }, [user, loginTimestamp, navigate]);

  /**
   * Set auth checking to false on mount
   */
  useEffect(() => {
    setIsCheckingAuth(false);
  }, []);

  const value: AuthContextType = {
    isCheckingAuth,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;